import { Col, Form, Select } from "antd";
import { SALES_CONTRACT_COMMERCIAL_LIST } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import moment from "moment";

// Round 2 (B, R3): the page's non-destructive "load preview" handler (previewFunction,
// defined in SalesContractCostReport/index.js) is never wired into common/SearchForm's
// onFinish switch for this filterName (that switch routes to the Export/download
// handler instead), and SearchForm/CustomFilter/FilterComponent are out of this task's
// allow-list, so there is no prop path to reach it from here. The page provides it via
// this context instead of prop-drilling through files this task must not touch.
export const SalesContractCostReportPreviewContext = createContext(() => {});

const SalesContractCostReportFields = ({ initialTableData }) => {
  const { Option } = Select;
  const form = Form.useFormInstance();
  const previewFunction = useContext(SalesContractCostReportPreviewContext);
  const [salesContracts, setSalesContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  const didSeedDefaultRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getSalesContracts = async (filter) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const body = {
      page: 1,
      per_page: 100,
      ...(filter?.reference_no && { reference_no: filter.reference_no }),
    };
    const response = await getData(
      `${SALES_CONTRACT_COMMERCIAL_LIST}`,
      null,
      body,
    );

    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.status === 200) {
      setSalesContracts(response?.data?.data?.data || []);
    }
    setHasLoadedOnce(true);
    setLoading(false);
  };

  // on search sales contracts
  const onSalesContractSearch = useCallback(async (value) => {
    if (value && value.length >= 2) {
      await getSalesContracts({ reference_no: value });
    } else if (!value) {
      await getSalesContracts();
    }
  }, []);

  useEffect(() => {
    getSalesContracts();
  }, []);

  // Round 2 (B, R3): once the option list is in, pre-select the commercial record
  // whose sale_contract_info.updated_at is newest among the loaded (<=100) rows and
  // load its preview the same way a manual selection would (previewFunction is a
  // plain GET into local state, no export/side-effect). Only ever seeds once, and
  // only on the initial (unfiltered) load — a user's own search must not be overridden.
  useEffect(() => {
    if (didSeedDefaultRef.current) return;
    if (!hasLoadedOnce) return;
    didSeedDefaultRef.current = true;
    if (!salesContracts.length) return;

    const mostRecent = salesContracts.reduce((best, current) => {
      const bestTime = best?.sale_contract_info?.updated_at
        ? moment(best.sale_contract_info.updated_at).valueOf()
        : -Infinity;
      const currentTime = current?.sale_contract_info?.updated_at
        ? moment(current.sale_contract_info.updated_at).valueOf()
        : -Infinity;
      return currentTime > bestTime ? current : best;
    }, salesContracts[0]);

    const defaultId = mostRecent?.sale_contract_info?.id;
    if (!defaultId) return;

    form?.setFieldsValue({ sales_contract_id: defaultId });
    previewFunction({ sales_contract_id: defaultId });
  }, [hasLoadedOnce, salesContracts, form, previewFunction]);

  return (
    <>
      <Col span={6}>
        <Form.Item label="Sales Contract No" name="sales_contract_id">
          <Select
            size="small"
            placeholder="Select Sales Contract"
            loading={loading}
            showSearch
            optionFilterProp="children"
            onSearch={onSalesContractSearch}
            allowClear
            filterOption={false}
            notFoundContent={
              hasLoadedOnce && !loading
                ? "No sales contract commercial records found to pre-select."
                : undefined
            }
          >
            {salesContracts.map((contract) => (
              <Option
                key={contract.sale_contract_info.id}
                value={contract.sale_contract_info.id}
              >
                {contract.sale_contract_info?.reference_no}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default SalesContractCostReportFields;
